import { useEffect, useState, useMemo, useCallback } from 'react';
import useFiltersState from 'state/useFiltersState';
import { ISharedModules, ORDER_PROPERTIES_TO_SEARCH } from 'types/orders';
import { bordColorTheme } from 'styles/styles.constants';
import ChargerModule from 'components/chargerModule';
import CustomSelect from 'components/customSelect';
import OrdersListingFinances from './components/OrdersListingFinances/OrdersListingFinances';
import OrdersListingOperations from './components/OrdersListingOperations/OrdersListingOperations';
import './TrackingOrder.scss';

enum AREA_USER {
  FINANCES = 'Finanzas',
  OPERATIONS = 'Operación'
}

/**
 * Componente principal para el tracking de órdenes.
 * Muestra las órdenes en formato Trello con dos módulos: Finanzas y Operaciones.
 * Cada módulo hijo maneja sus propias llamadas API de forma independiente.
 */
const TrackingOrder = () => {
  const {
    numberOfOrders,
    sharedModule,
    setSharedModule,
    ordersSearchbarText,
    setOrdersSearchbarText,
    propertyToFilterSearch,
    setPropertyToFilterSearch
  } = useFiltersState();

  const initialSharedModules = useMemo<ISharedModules[]>(
    () => [
      { title: AREA_USER.FINANCES, key: 1, active: true },
      { title: AREA_USER.OPERATIONS, key: 2, active: false }
    ],
    []
  );

  const [sharedModules, setSharedModules] = useState<ISharedModules[]>(initialSharedModules);

  const headerSubtitle = `Compras ${typeof numberOfOrders === 'number' ? numberOfOrders : ''}`;
  const headerInfo = { title: 'Órdenes', titleRoute: '/tracking-order/main', subtitle: headerSubtitle };
  const isModuleFirstKey = sharedModule?.key === 1;
  const orderPropertiesToSearchValues = Object.values(ORDER_PROPERTIES_TO_SEARCH);
  const orderPropertiesToSearchKeys = Object.keys(ORDER_PROPERTIES_TO_SEARCH);

  /**
   * Maneja el cambio de propiedad de búsqueda en el filtro.
   * @param value - Valor de la propiedad seleccionada
   */
  const handleSetPropertyToFilterSearch = (value: ORDER_PROPERTIES_TO_SEARCH) =>
    setPropertyToFilterSearch({
      name: value,
      propertyToFilter: orderPropertiesToSearchKeys?.find(
        key => ORDER_PROPERTIES_TO_SEARCH[key as keyof typeof ORDER_PROPERTIES_TO_SEARCH] === value
      )
    });

  const orderSearchValuesSet = useMemo(() => new Set<string>(Object.values(ORDER_PROPERTIES_TO_SEARCH)), []);

  const isOrderPropertyToSearch = (value: string): value is ORDER_PROPERTIES_TO_SEARCH =>
    orderSearchValuesSet.has(value);

  const handleSearchTypeSelect = (value: string) => {
    if (isOrderPropertyToSearch(value)) handleSetPropertyToFilterSearch(value);
  };

  /**
   * Obtiene el placeholder del searchbar según el tipo de búsqueda seleccionado.
   * @returns Texto placeholder para el searchbar
   */
  const getSearchbarPlaceholder = () => {
    switch (propertyToFilterSearch?.name) {
      case ORDER_PROPERTIES_TO_SEARCH.ORGANIZATION_NAME:
        return 'Escribe el nombre de empresa';
      case ORDER_PROPERTIES_TO_SEARCH.SERIAL_NUMBER:
        return 'Escribe el número del serial';
      case ORDER_PROPERTIES_TO_SEARCH.SKU_NUMBER:
        return 'Escribe el número de SKU';
      case ORDER_PROPERTIES_TO_SEARCH.EMPLOYEE_NAME:
        return 'Escribe el nombre del empleado';
      case ORDER_PROPERTIES_TO_SEARCH.PURCHASE_ORDER:
        return 'Escribe el número de la orden de compra (PO)';
      default:
        return 'Escribe el número de orden';
    }
  };

  /**
   * Cambia el módulo activo al que corresponde a la key proporcionada.
   * @param key - Key del módulo a activar
   */
  const sharedModulesAction = (key: number) => {
    const newList: ISharedModules[] = [];
    for (const module of sharedModules) {
      const newModule = {
        key: module.key,
        title: module.title,
        active: module.key === key
      };
      if (newModule.active) setSharedModule(newModule);
      newList.push(newModule);
    }
    setSharedModules([...newList]);
  };

  /**
   * Sincroniza los módulos compartidos con los permisos del usuario.
   */
  const syncSharedModules = useCallback(() => {
    setSharedModules(initialSharedModules);
    const activeModule = initialSharedModules.find(module => module.active);
    const currentModuleExists = initialSharedModules.some(module => module.key === sharedModule?.key);

    if (activeModule && (!sharedModule || !currentModuleExists)) {
      setSharedModule(activeModule);
    }
  }, [initialSharedModules, sharedModule, setSharedModule]);

  useEffect(() => {
    syncSharedModules();
  }, [syncSharedModules]);

  return (
    <div id="trackingOrderModule">
      <ChargerModule
        infoSideBar={{ position: 'trackingOrder' }}
        headerInfo={headerInfo}
        viewSearchBar
        searchBarDropdownList={
          <CustomSelect
            customClassName="customSelectTrackingOrder"
            containerOptionsClassName="containerOptionsTrackingOrder"
            customBackground={bordColorTheme.white.default}
            customBackgroundEmpty={bordColorTheme.white.default}
            dropdownLabel="Tipo de búsqueda"
            items={orderPropertiesToSearchValues}
            value={propertyToFilterSearch?.name}
            onChangeSelect={handleSearchTypeSelect}
          />
        }
        searchText={ordersSearchbarText}
        setSearchText={setOrdersSearchbarText}
        searchbarPlaceholder={getSearchbarPlaceholder()}
        sharedModules={sharedModules}
        sharedModule={sharedModule}
        sharedModulesAction={sharedModulesAction}
      >
        {isModuleFirstKey ? <OrdersListingFinances /> : <OrdersListingOperations />}
      </ChargerModule>
    </div>
  );
};

export default TrackingOrder;
